import React from "react";

/**
 * The Production Agent Workspace frame
 * (WORKSPACE-FEATURE-001 S5.1; authoritative original composition at
 * commit 23d500e27; reference
 * `docs/design/prototypes/workspace-v1-reference.png`).
 *
 * The authoritative desktop workspace is three stacked regions over two
 * horizontal work areas:
 *
 *   1. operational row   -- KPIs, campaign identity/state, campaign controls
 *   2. main workspace    -- center tabs beside the right tools region
 *   3. bottom call bar   -- call identity, condition, call controls
 *
 * This component is that stack and nothing else: three slots, no state,
 * no conditionals, no knowledge of the dialer lifecycle. Every decision
 * about what goes in a slot stays in `AgentWorkspaceProductionShell`.
 *
 * AW-VIS-2 COLLAPSED PR-A'S HEADER AND KPI SLOTS INTO ONE
 * -------------------------------------------------------
 * PR-A read S5.1's "four stacked layers" literally and gave the header
 * and the KPI summary separate slots; PR-A-VIS-1 then wrapped both in
 * one glass card to stop them reading as two bands. Neither matched the
 * authoritative design, which has one operational row carrying both --
 * KPIs at the left, campaign identity and controls at the right edge of
 * the same row (S8.3: they "read as one conceptual group").
 *
 * So there is now one slot instead of two, and the card lives in
 * `WorkspaceOperationalRow` itself rather than here. The semantic and
 * test identities of both regions survive inside it: `workspace-
 * kpi-region` and the campaign section are still separately
 * addressable, because they remain different regions with different
 * future owners.
 *
 * WHY THE STACK IS ITS OWN COMPONENT
 * ----------------------------------
 * S5.3 (stable placement) and S5.4 (workspace continuity) are
 * structural guarantees: high-frequency controls occupy stable
 * locations, and a call-state change must alter content and control
 * availability without relocating or reconstructing a region. Those are
 * far easier to hold -- and to test -- when the region order is
 * expressed once, declaratively, in a component that cannot branch on
 * call state because it is never given it.
 *
 * The row and call bar are `shrink-0`; only the body grows. That is
 * what keeps them available on supported desktop layouts (S5.6) while
 * the center workspace scrolls internally.
 */
export default function WorkspaceFrame({ operationalRow, body, callBar }) {
  return (
    <div
      className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden p-3"
      data-testid="workspace-frame"
    >
      {operationalRow}
      {body}
      {callBar}
    </div>
  );
}
